export const PPTLayoutOptions = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
  COMPACT: 'compact'
}

export const PPTLayoutLabels = {
  [PPTLayoutOptions.HORIZONTAL]: 'H',
  [PPTLayoutOptions.VERTICAL]: 'V',
  [PPTLayoutOptions.COMPACT]: 'C'
}

export const PPTLevelsPerSlide = {
  TWO: '2',
  THREE: '3'
}

export const PPTLevelsLabels = {
  [PPTLevelsPerSlide.TWO]: '2/Slide',
  [PPTLevelsPerSlide.THREE]: '3/Slide'
}

export const PPTCardContentOptions = {
  FOCUSED: 'focused',
  CUSTOM: 'custom'
}

export const PPTCardContentLabels = {
  [PPTCardContentOptions.FOCUSED]: {
    label: 'Focused',
    description: 'Position title only'
  },
  [PPTCardContentOptions.CUSTOM]: {
    label: 'Custom',
    description: 'App selections'
  }
}

// Map org-chart highlight field IDs to the theme category keys expected by PPT export.
export const PPT_THEME_CATEGORY_BY_HIGHLIGHT_MODE = Object.freeze({
  department: 'departments',
  officeLocation: 'locations',
  role: 'roles',
  level: 'depths'
})

/**
 * Resolve a highlight field ID to its PPT theme category.
 *
 * @param {string|null|undefined} mode - Org-chart fill or outline highlight field ID.
 * @returns {string|null} The forecast theme category, or null when no mode is selected.
 */
export function getPptThemeCategory(mode) {
  if (!mode) return null
  return PPT_THEME_CATEGORY_BY_HIGHLIGHT_MODE[mode] || 'custom fields'
}

/**
 * Convert technical PPT request failures into safe, actionable toast messages.
 *
 * @param {{response?: {status?: number}, code?: string}|null|undefined} error
 *   Axios-style request error.
 * @returns {string} User-facing PPT export failure guidance.
 */
export function getPptExportErrorMessage(error) {
  const status = error?.response?.status

  if (status === 413) {
    return 'The PowerPoint export API payload is too large. Reduce the selected layers or card fields and try again.'
  }
  if (status === 403) {
    return "You don't have permission to export this PowerPoint."
  }
  if (status === 408 || status === 504 || error?.code === 'ECONNABORTED') {
    return 'The PowerPoint export timed out. Reduce the selected layers or card fields and try again.'
  }
  if (status === 429) {
    return 'Too many PowerPoint exports are in progress. Please wait and try again.'
  }

  return 'Failed to export PowerPoint. Please try again. If the problem persists, contact support.'
}

/**
 * Select only colors used by PPT fill/outline highlights.
 *
 * The shared theme can contain values from every custom field. Sending that
 * complete map makes the export body grow past the API's JSON size limit, and
 * each color is expanded further by the export service before transmission.
 *
 * @param {Object} options - Theme data and active highlight fields.
 * @param {Record<string, Record<string, string>>|null|undefined} options.themeColors
 *   Complete theme map generated by useTheme.
 * @param {string|null|undefined} options.highlightMode - Fill highlight field ID.
 * @param {string|null|undefined} options.outlineHighlightMode - Outline highlight field ID.
 * @param {Record<string, Array<unknown>>} [options.customFieldUniqueValues]
 *   Unique values keyed by custom-field ID.
 * @returns {Record<string, Record<string, string>>|null}
 *   A new filtered theme map, or null when neither mode has usable colors.
 */
export function filterPptThemeColors({
  themeColors,
  highlightMode,
  outlineHighlightMode,
  customFieldUniqueValues = {}
}) {
  if (!themeColors) return null

  const filteredThemeColors = {}
  const customValues = new Set()
  const modes = new Set([highlightMode, outlineHighlightMode].filter(Boolean))

  for (const mode of modes) {
    const systemCategory = PPT_THEME_CATEGORY_BY_HIGHLIGHT_MODE[mode]
    if (systemCategory) {
      const categoryColors = themeColors[systemCategory]
      if (categoryColors && Object.keys(categoryColors).length > 0) {
        filteredThemeColors[systemCategory] = { ...categoryColors }
      }
      continue
    }

    const values = customFieldUniqueValues[mode]
    if (Array.isArray(values)) {
      values.forEach((value) => customValues.add(value))
    }
  }

  const customThemeColors = themeColors['custom fields']
  if (customThemeColors && customValues.size > 0) {
    const selectedCustomColors = Object.create(null)
    for (const value of customValues) {
      if (Object.prototype.hasOwnProperty.call(customThemeColors, value)) {
        selectedCustomColors[value] = customThemeColors[value]
      }
    }
    if (Object.keys(selectedCustomColors).length > 0) {
      filteredThemeColors['custom fields'] = selectedCustomColors
    }
  }

  return Object.keys(filteredThemeColors).length > 0 ? filteredThemeColors : null
}
